import { Link, useParams } from 'react-router-dom';

import { useGetInconsistenciesByIds } from '@/api/queries/useGetInconsistenciesById';
import { useGetMatterV2 } from '@/api/queries/useGetMatterV2';
import { useGetSubset } from '@/api/queries/useGetSubset';
import { Breadcrumb } from '@/new/components/breadcrumb';
import { Icon } from '@/new/components/icon';
import { InconsistencyCard } from '@/new/components/inconsistency/inconsistency-card';
import { PageContainer } from '@/new/components/page-container';
import { PageHeader } from '@/new/components/page-header';
import { PageTitle } from '@/new/components/page-title';
import { Button } from '@/new/components/shadcn/button';
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/new/components/shadcn/empty';
import { SubsetChip } from '@/new/components/subset-chip';
import { useSubsetBreadcrumbs } from '@/new/hooks/use-subset-breadcrumbs';
import { InconsistenciesLoader } from '@/new/pages/matters/:matterId/subsets/:subsetId/inconsistencies/components/inconsistencies-loader';

export function InconsistencyPage() {
  const { matterId, subsetId, inconsistencyId } = useParams();
  if (!matterId || !subsetId || !inconsistencyId) return null;

  const { data: matter } = useGetMatterV2(matterId);
  const { data: subset } = useGetSubset(matterId, subsetId);
  const { data: inconsistencies, isLoading } = useGetInconsistenciesByIds({
    matterId,
    inconsistencyIds: [inconsistencyId],
  });

  const inconsistency = inconsistencies?.[0];

  const breadcrumbItems = useSubsetBreadcrumbs({
    matterId,
    subsetId,
    matter,
    subset,
    currentPage: { label: 'Inconsistencies', path: 'inconsistencies' },
  });

  return (
    <>
      <Breadcrumb
        items={[
          ...breadcrumbItems.map((item, index) =>
            index === breadcrumbItems.length - 1 ? { ...item, active: false } : item,
          ),
          {
            href: `/matters/${matterId}/subsets/${subsetId}/inconsistencies/${inconsistencyId}`,
            label: 'Inconsistency',
            active: true,
          },
        ]}
      />

      <div className="flex h-full flex-col overflow-hidden">
        <PageContainer>
          <PageHeader>
            <div className="items flex gap-4">
              <PageTitle text="Inconsistency" />
              <SubsetChip subsetId={subsetId} className="mt-0.5" />
            </div>

            <Button asChild size={'sm'} className="rounded-full pr-2.5">
              <Link to={`/matters/${matterId}/subsets/${subsetId}/inconsistencies`}>
                <Icon className="lucide--arrow-left" />
                Back to all inconsistencies
              </Link>
            </Button>
          </PageHeader>
        </PageContainer>

        {isLoading ? (
          <InconsistenciesLoader />
        ) : inconsistency ? (
          <div className="mx-5 overflow-auto">
            <InconsistencyCard inconsistency={inconsistency} renderAsAccordion={false} />
          </div>
        ) : (
          <Empty className="mb-32">
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <Icon className="lucide--alert-triangle" />
              </EmptyMedia>
              <EmptyTitle>Inconsistency not found</EmptyTitle>
              <EmptyDescription>This inconsistency is unavailable or may have been deleted.</EmptyDescription>
            </EmptyHeader>
          </Empty>
        )}
      </div>
    </>
  );
}
